import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from, of } from "rxjs";
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { currentUserData } from "../selectors/authentication.selectors";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { ScenariosStoreAction, StorageNativeStoreAction } from "src/components/shared-components/enum/enumStoreAction";
import {
  scenarioDeletedAction,
  scenarioInsertedAction,
  scenariosLoadErrorAction,
  scenariosLoadLastModifiedFromApiAction,
  scenariosLoadListFromApiAction,
  scenariosLoadSuccessAction,
  scenariosUnsetKeyStorageAction,
  scenarioUpdatedAction,
} from "../actions/scenario.action";
import { listScenarios } from "../selectors/scenario.selector";
import { ScenariosService } from "src/service/scenarios.service";
import { Scenario } from "src/components/shared-components/class/general-classes/scenario";
import { EventService } from "src/service/event.service";

/**
 * Handle generic side ngrx effect from scenarios action
 */
@Injectable()
export class ScenarioEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _scenariosService = inject(ScenariosService);
  private readonly _eventService = inject(EventService);
  private readonly _store = inject(Store);


  /**
   * Effect who catch main event and unset specific server key storage
   */
  public scenariosUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => scenariosUnsetKeyStorageAction())
    )
  );

  /**
   * Effect attached to ngrx store init, then start init initialization
   */
  public scenarioStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIOS_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listScenarios)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const scenarios: GenericStoreApiData = combinedObject[2];

        if (scenarios?.data) {
          return scenariosLoadLastModifiedFromApiAction({
            scenarioObject: scenarios.data,
          });
        } else {
          return scenariosLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of scenarios effect
   */
  public scenarioListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIOS_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._scenariosService.getScenarios().pipe(
          map((scenarioObject: GenericApiData) =>
            scenariosLoadSuccessAction({ scenarioObject })
          ),
          catchError((error: any) =>
            of(scenariosLoadErrorAction({ errorCode: error?.status ?? 0 }))
          )
        );
      })
    )
  );

  /**
   * Load full list of scenario by last modified effect
   */
  public scenarioListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIOS_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._scenariosService.getScenariosByLastModified(action.scenarioObject).pipe(
          map((scenarioObject: GenericApiData) =>
            scenariosLoadSuccessAction({ scenarioObject })
          ),
          catchError((error: any) =>
            of(scenariosLoadErrorAction({ errorCode: error?.status ?? 0 }))
          )
        );
      })
    )
  );

  /**
   * Add the inserted scenario to the store when receiving the MQTT message
   */
  public scenarioInsertReceivedEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIO_INSERT_RECEIVED),
      switchMap((action: any) =>
        from(this._scenariosService.getScenarioByID(action.idScenario)).pipe(
          map((scenario: Scenario) =>
            scenarioInsertedAction({ scenario, clientID: action.clientID })
          ),
          catchError(() => of({ type: "[Scenarios] Insert MQTT fetch error" }))
        )
      )
    )
  );

  /**
   * Update the scenario in the store when receiving the MQTT message
   */
  public scenarioUpdateReceivedEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIO_UPDATE_RECEIVED),
      switchMap((action: any) =>
        from(this._scenariosService.getScenarioByID(action.idScenario)).pipe(
          withLatestFrom(this._store.select(listScenarios)),
          filter(([_, scenarios]: [Scenario, GenericStoreApiData]) =>
            !!scenarios?.data?.data?.some(
              (item: Scenario) => item.id === action.idScenario
            )
          ),
          map(([scenario]: [Scenario, GenericStoreApiData]) =>
            scenarioUpdatedAction({ scenario, clientID: action.clientID })
          ),
          catchError(() => of({ type: "[Scenarios] Update MQTT fetch error" }))
        )
      )
    )
  );

  /**
   * Delete the scenario from the store when receiving the MQTT message
   */
  public scenarioDeleteReceivedEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosStoreAction.SCENARIO_DELETE_RECEIVED),
      map((action: any) =>
        scenarioDeletedAction({
          idScenario: action.idScenario,
          clientID: action.clientID,
        })
      )
    )
  );

  /**
   * Notify components subscribed to OnScenarioChange after a scenario mutation
   */
  public scenarioChangeNotifyEffect = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          ScenariosStoreAction.SCENARIO_INSERTED,
          ScenariosStoreAction.SCENARIO_UPDATED,
          ScenariosStoreAction.SCENARIO_DELETED
        ),
        debounceTime(1000),
        tap((action: any) => {
          const idScenario = action.scenario?.id ?? action.idScenario;
          this._eventService.OnScenarioChange.next(
            idScenario + "|" + action.clientID
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * Notify components subscribed to OnScheduleChange after a schedule has been
   * linked or unlinked from a scenario (debounced like above).
   */
  public scenarioScheduleChangeNotifyEffect = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          ScenariosStoreAction.SCENARIO_SCHEDULE_ASSIGNED,
          ScenariosStoreAction.SCENARIO_SCHEDULE_UNASSIGNED
        ),
        debounceTime(1000),
        tap((action: any) => {
          this._eventService.OnScheduleChange.next(
            action.idBigLogic + "|" + action.clientID
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * After the scenarios list is loaded from API, subscribe to the MQTT
   * "Running" topic of every scenario so that running flag updates are
   * propagated to the store.
   */
  public scenariosSubscribeRunningOnLoadEffect = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ScenariosStoreAction.SCENARIOS_LOAD_SUCCESS),
        tap((action: any) => {
          const scenarios: Array<Scenario> = action.scenarioObject?.data || [];
          for (const scenario of scenarios) {
            this._scenariosService.subscribeScenarioRunningEvent(scenario);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * When a brand-new scenario is added to the store via MQTT, subscribe to its
   * running topic too.
   */
  public scenarioSubscribeRunningOnInsertEffect = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ScenariosStoreAction.SCENARIO_INSERTED),
        tap((action: any) => {
          if (action.scenario) {
            this._scenariosService.subscribeScenarioRunningEvent(
              action.scenario
            );
          }
        })
      ),
    { dispatch: false }
  );
}
